import React, { useState, useEffect } from 'react';
import { InvitationDesign } from '../../../_types/invitation-design-v2';
import { HexColorPicker } from 'react-colorful';

interface BackgroundTabProps {
  design: InvitationDesign;
  onTemplateUpdate: (updates: any) => void;
}

const PATTERN_OPTIONS = [
  { id: 'dots', name: 'Dots', url: '/assets/textures/45-degree-fabric-light.png' },
  { id: 'linen', name: 'Linen', url: '/assets/textures/asfalt-light.png' },
  { id: 'paper', name: 'Paper', url: '/assets/textures/paper.png' },
  { id: 'floral', name: 'Floral', url: '/assets/textures/flowers.png' },
  { id: 'vintage', name: 'Vintage', url: '/assets/textures/old-wall.png' },
  { id: 'subtle', name: 'Subtle', url: '/assets/textures/subtle-white-feathers.png' },
  { id: 'fabric', name: 'Fabric', url: '/assets/textures/white-linen.png' },
  { id: 'elegant', name: 'Elegant', url: '/assets/textures/diamond-upholstery.png' }
];

const GRADIENT_PRESETS = [
  { id: 'sunset', name: 'Sunset', value: 'linear-gradient(135deg, #FFE5CC 0%, #FFD4A3 40%, #FFC19E 70%, #FFB397 100%)' },
  { id: 'ocean', name: 'Ocean', value: 'linear-gradient(to bottom, #E3F2FD 0%, #BBDEFB 30%, #90CAF9 60%, #64B5F6 100%)' },
  { id: 'rose', name: 'Rose Garden', value: 'linear-gradient(135deg, #FFF5F7 0%, #FFE0E6 50%, #FFF5F7 100%)' },
  { id: 'golden', name: 'Golden', value: 'radial-gradient(circle at center, #FFFEF5 0%, #FFF8DC 40%, #FFEBAA 80%, #FFE4AA 100%)' },
  { id: 'silver', name: 'Silver', value: 'linear-gradient(135deg, #FAFAFA 0%, #E8E8E8 25%, #F5F5F5 50%, #E8E8E8 75%, #FAFAFA 100%)' },
  { id: 'rainbow', name: 'Rainbow', value: 'linear-gradient(45deg, #FFE5E5 0%, #FFE5F1 16%, #FFEFF5 32%, #E5F3FF 48%, #E5FFE5 64%, #FFFAE5 80%, #FFE5E5 100%)' },
  { id: 'mint', name: 'Mint Fresh', value: 'linear-gradient(to bottom right, #E8F5E9 0%, #C8E6C9 50%, #A5D6A7 100%)' },
  { id: 'lavender', name: 'Lavender', value: 'radial-gradient(ellipse at top, #F3E5F5 0%, #E1BEE7 50%, #CE93D8 100%)' }
];

const BackgroundTab: React.FC<BackgroundTabProps> = ({
  design,
  onTemplateUpdate,
}) => {
  const [showBorderColorDropdown, setShowBorderColorDropdown] = useState(false);
  const [showBorderColorPicker, setShowBorderColorPicker] = useState(false);
  const [customGradient, setCustomGradient] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  
  // Click outside handler for border color dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.border-color-dropdown-container')) {
        setShowBorderColorDropdown(false);
        setShowBorderColorPicker(false);
      }
    };
    
    if (showBorderColorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showBorderColorDropdown]);

  return (
    <div className="space-y-6">
      {/* Background Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Background</h3>
        
        {/* Background Type */}
        <div className="mb-4">
          <label className="block text-xs text-gray-600 mb-2">Background Type</label>
          <select
            value={design.template.background.type}
            onChange={(e) => {
              const newType = e.target.value as any;
              const currentBackground = design.template.background;
              
              // Preserve the background color when switching types
              let preservedColor = '#FFFFFF'; // fallback
              if (currentBackground.type === 'solid') {
                preservedColor = currentBackground.value || '#FFFFFF';
              } else if (currentBackground.backgroundColor) {
                preservedColor = currentBackground.backgroundColor;
              }
              
              const updatedBackground = {
                ...currentBackground,
                type: newType
              };
              
              // Set the appropriate color property based on the new type
              if (newType === 'solid') {
                updatedBackground.value = preservedColor;
                updatedBackground.backgroundColor = preservedColor;
              } else if (newType === 'gradient') {
                updatedBackground.value = GRADIENT_PRESETS[0].value;
                updatedBackground.backgroundColor = preservedColor;
              } else if (newType === 'image') {
                updatedBackground.value = '';
                updatedBackground.backgroundColor = preservedColor;
              } else {
                // For pattern type
                updatedBackground.backgroundColor = preservedColor;
              }
              
              onTemplateUpdate({ background: updatedBackground });
            }}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
          >
            <option value="solid">Solid Color</option>
            <option value="gradient">Gradient</option>
            <option value="pattern">Pattern</option>
            <option value="image">Image</option>
          </select>
        </div>

        {/* Solid Color Background */}
        {design.template.background.type === 'solid' && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Background Color</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={design.template.background.value || '#FFFFFF'}
                onChange={(e) => {
                  onTemplateUpdate({
                    background: {
                      ...design.template.background,
                      value: e.target.value
                    }
                  });
                }}
                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={design.template.background.value || '#FFFFFF'}
                onChange={(e) => {
                  onTemplateUpdate({
                    background: {
                      ...design.template.background,
                      value: e.target.value
                    }
                  });
                }}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
              />
            </div>
          </div>
        )}

        {/* Gradient Background */}
        {design.template.background.type === 'gradient' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-2">Gradient Presets</label>
              <div className="grid grid-cols-2 gap-2">
                {GRADIENT_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => onTemplateUpdate({
                      background: {
                        ...design.template.background,
                        value: preset.value
                      }
                    })}
                    className={`p-2 border rounded-md hover:border-green-500 transition-colors ${
                      design.template.background.value === preset.value ? 'border-green-500 bg-green-50' : 'border-gray-300'
                    }`}
                    title={preset.name}
                  >
                    <div 
                      className="w-full h-12 rounded"
                      style={{
                        background: preset.value
                      }}
                    />
                    <span className="text-xs text-gray-600 mt-1 block">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Custom Gradient</label>
              <textarea
                value={customGradient || design.template.background.value}
                onChange={(e) => setCustomGradient(e.target.value)}
                placeholder="e.g., linear-gradient(45deg, #ff0000, #0000ff)"
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded h-20 font-mono"
              />
              <button
                onClick={() => {
                  if (customGradient) {
                    onTemplateUpdate({
                      background: {
                        ...design.template.background,
                        value: customGradient
                      }
                    });
                  }
                }}
                className="mt-2 px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
              >
                Apply Custom Gradient
              </button>
              <p className="text-xs text-gray-500 mt-1">
                Enter a CSS gradient value (linear-gradient, radial-gradient, etc.)
              </p>
            </div>
          </div>
        )}

        {/* Pattern Background */}
        {design.template.background.type === 'pattern' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Base Color for Pattern</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={design.template.background.backgroundColor || '#FFFFFF'}
                  onChange={(e) => {
                    onTemplateUpdate({
                      background: {
                        ...design.template.background,
                        backgroundColor: e.target.value
                      }
                    });
                  }}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={design.template.background.backgroundColor || '#FFFFFF'}
                  onChange={(e) => {
                    onTemplateUpdate({
                      background: {
                        ...design.template.background,
                        backgroundColor: e.target.value
                      }
                    });
                  }}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-2">Select Pattern</label>
              <div className="grid grid-cols-4 gap-2">
                {PATTERN_OPTIONS.map((pattern) => (
                  <button
                    key={pattern.id}
                    onClick={() => onTemplateUpdate({
                      background: {
                        ...design.template.background,
                        value: pattern.url
                      }
                    })}
                    className={`p-2 border rounded-md hover:border-green-500 transition-colors ${
                      design.template.background.value === pattern.url ? 'border-green-500 bg-green-50' : 'border-gray-300'
                    }`}
                    title={pattern.name}
                  >
                    <div 
                      className="w-full h-12 rounded"
                      style={{
                        backgroundImage: `url(${pattern.url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundColor: design.template.background.backgroundColor || '#FFFFFF'
                      }}
                    />
                    <span className="text-xs text-gray-600 mt-1 block">{pattern.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Pattern Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={design.template.background.opacity || 0.5}
                onChange={(e) => onTemplateUpdate({
                  background: {
                    ...design.template.background,
                    opacity: parseFloat(e.target.value)
                  }
                })}
                className="w-full"
              />
              <span className="text-xs text-gray-500">{((design.template.background.opacity || 0.5) * 100).toFixed(0)}%</span>
            </div>
          </div>
        )}

        {/* Image Background */}
        {design.template.background.type === 'image' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Image URL</label>
              <input
                type="text"
                value={imageUrl || design.template.background.value}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              />
              <button
                onClick={() => {
                  if (imageUrl) {
                    onTemplateUpdate({
                      background: {
                        ...design.template.background,
                        value: imageUrl
                      }
                    });
                  }
                }}
                className="mt-2 px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
              >
                Apply Image
              </button>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Image Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={design.template.background.opacity || 1}
                onChange={(e) => onTemplateUpdate({
                  background: {
                    ...design.template.background,
                    opacity: parseFloat(e.target.value)
                  }
                })}
                className="w-full"
              />
              <span className="text-xs text-gray-500">{((design.template.background.opacity || 1) * 100).toFixed(0)}%</span>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Fallback Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={design.template.background.backgroundColor || '#FFFFFF'}
                  onChange={(e) => {
                    onTemplateUpdate({
                      background: {
                        ...design.template.background,
                        backgroundColor: e.target.value
                      }
                    });
                  }}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={design.template.background.backgroundColor || '#FFFFFF'}
                  onChange={(e) => {
                    onTemplateUpdate({
                      background: {
                        ...design.template.background,
                        backgroundColor: e.target.value
                      }
                    });
                  }}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        )}

        {/* Opacity Control for Gradient and Solid */}
        {(design.template.background.type === 'gradient' || design.template.background.type === 'solid') && (
          <div className="mt-3">
            <label className="block text-xs text-gray-600 mb-1">Background Opacity</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={design.template.background.opacity || 1}
              onChange={(e) => onTemplateUpdate({
                background: {
                  ...design.template.background,
                  opacity: parseFloat(e.target.value)
                }
              })}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{((design.template.background.opacity || 1) * 100).toFixed(0)}%</span>
          </div>
        )}
      </div>

      {/* Border Section */}
      {design.template.border && (
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Border</h3>
          <div>
            <label className="block text-xs text-gray-600 mb-2">
              Border Style
            </label>
            <select
              value={design.template.border.style}
              onChange={(e) => onTemplateUpdate({
                border: {
                  ...design.template.border!,
                  style: e.target.value as any
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="none">None</option>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
              <option value="double">Double</option>
              <option value="ornamental">Ornamental</option>
            </select>
          </div>

          {design.template.border.style !== 'none' && (
            <>
              <div className="relative border-color-dropdown-container">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Border Color
                </label>
                <button
                  onClick={() => setShowBorderColorDropdown(!showBorderColorDropdown)}
                  className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:border-gray-400 bg-white"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded border border-gray-300"
                      style={{ backgroundColor: design.template.border.color || design.template.theme.primaryColor }}
                    />
                    <span className="text-sm">
                      {design.template.border.color === design.template.theme.primaryColor ? 'Primary' :
                       design.template.border.color === design.template.theme.secondaryColor ? 'Secondary' :
                       design.template.border.color === design.template.theme.textColor ? 'Text' :
                       design.template.border.color === design.template.theme.accentColor ? 'Accent' :
                       'Custom'}
                    </span>
                  </div>
                  <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform ${showBorderColorDropdown ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Color Dropdown */}
                {showBorderColorDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                    {/* Theme Colors */}
                    <div className="p-3">
                      <div className="text-xs text-gray-500 font-medium mb-2">Theme Colors</div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { name: 'Primary', color: design.template.theme.primaryColor },
                          { name: 'Secondary', color: design.template.theme.secondaryColor },
                          { name: 'Text', color: design.template.theme.textColor },
                          { name: 'Accent', color: design.template.theme.accentColor },
                        ].map((themeColor) => (
                          <button
                            key={themeColor.name}
                            onClick={() => {
                              onTemplateUpdate({
                                border: {
                                  ...design.template.border!,
                                  color: themeColor.color
                                }
                              });
                              setShowBorderColorDropdown(false);
                            }}
                            className={`flex items-center gap-2 p-2 rounded hover:bg-gray-50 transition-colors ${
                              design.template.border.color === themeColor.color ? 'bg-rose-50' : ''
                            }`}
                          >
                            <div 
                              className="w-6 h-6 rounded border border-gray-300"
                              style={{ backgroundColor: themeColor.color }}
                            />
                            <span className="text-sm">{themeColor.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-100" />
                    
                    {/* Custom Color Option */}
                    <button
                      onClick={() => setShowBorderColorPicker(!showBorderColorPicker)}
                      className={`w-full px-3 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        showBorderColorPicker ? 'bg-rose-50' : ''
                      }`}
                    >
                      <div className="w-6 h-6 rounded border-2 border-gray-300 bg-gradient-to-br from-red-500 via-green-500 to-blue-500" />
                      <span className="text-sm font-medium">Custom Color</span>
                    </button>
                    
                    {/* Color Picker */}
                    {showBorderColorPicker && (
                      <div className="p-4 border-t border-gray-100">
                        <HexColorPicker
                          color={design.template.border.color || design.template.theme.primaryColor}
                          onChange={(color) => onTemplateUpdate({
                            border: {
                              ...design.template.border!,
                              color
                            }
                          })}
                        />
                        <button
                          onClick={() => {
                            setShowBorderColorPicker(false);
                            setShowBorderColorDropdown(false);
                          }}
                          className="w-full mt-3 px-4 py-2 bg-rose-500 text-white rounded text-sm font-medium hover:bg-rose-600"
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Border Width
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={design.template.border.width}
                  onChange={(e) => onTemplateUpdate({
                    border: {
                      ...design.template.border!,
                      width: parseInt(e.target.value)
                    }
                  })}
                  className="w-full"
                />
                <span className="text-sm text-gray-500">{design.template.border.width}px</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Corner Radius
                </label>
                <input
                  type="range"
                  min="0"
                  max="32"
                  value={design.template.border.radius || 0}
                  onChange={(e) => onTemplateUpdate({
                    border: {
                      ...design.template.border!,
                      radius: parseInt(e.target.value)
                    }
                  })}
                  className="w-full"
                />
                <span className="text-sm text-gray-500">{design.template.border.radius || 0}px</span>
              </div>

              {/* Pattern Selection for Ornamental Borders */}
              {design.template.border.style === 'ornamental' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Border Pattern
                  </label>
                  <select
                    value={design.template.border.pattern || 'classic'}
                    onChange={(e) => onTemplateUpdate({
                      border: {
                        ...design.template.border!,
                        pattern: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <optgroup label="Classic">
                      <option value="classic">Classic Frame</option>
                      <option value="double-classic">Double Classic</option>
                      <option value="ornamental-classic">Ornamental Classic</option>
                    </optgroup>
                    <optgroup label="Floral">
                      <option value="floral-vine">Floral Vine</option>
                      <option value="floral-corners">Floral Corners</option>
                      <option value="roses">Rose Border</option>
                      <option value="leaves">Leaf Pattern</option>
                      <option value="botanical">Botanical Frame</option>
                    </optgroup>
                    <optgroup label="Geometric">
                      <option value="geometric">Geometric Pattern</option>
                      <option value="art-deco">Art Deco</option>
                      <option value="greek-key">Greek Key</option>
                      <option value="diamond">Diamond Pattern</option>
                      <option value="chevron">Chevron Border</option>
                    </optgroup>
                    <optgroup label="Vintage">
                      <option value="vintage">Vintage Frame</option>
                      <option value="baroque">Baroque Ornament</option>
                      <option value="victorian">Victorian Style</option>
                      <option value="filigree">Filigree Pattern</option>
                      <option value="scrollwork">Scrollwork</option>
                    </optgroup>
                    <optgroup label="Celtic">
                      <option value="celtic">Celtic Knot</option>
                      <option value="celtic-border">Celtic Border</option>
                      <option value="trinity">Trinity Knot</option>
                    </optgroup>
                    <optgroup label="Wedding">
                      <option value="wedding-bells">Wedding Bells</option>
                      <option value="hearts">Heart Border</option>
                      <option value="rings">Wedding Rings</option>
                      <option value="doves">Love Doves</option>
                      <option value="ribbon">Ribbon Frame</option>
                    </optgroup>
                    <optgroup label="Modern">
                      <option value="minimalist">Minimalist Lines</option>
                      <option value="dots">Dot Pattern</option>
                      <option value="stripes">Stripe Border</option>
                      <option value="waves">Wave Pattern</option>
                    </optgroup>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose a decorative pattern for ornamental borders
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BackgroundTab;